import { h, Fragment } from 'preact';
import { useState, useRef, useEffect, useMemo } from 'preact/hooks';
import Panel from 'utils/panel';
import { formatCartLinesAjax } from 'utils/storefront/format-resource';
import clsx from 'clsx';
import { formatMoney, getFormattedPriceObject } from '../utils/helpers';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import IconCart from '../utils/icon/cart.svg';
import CloseBig from '../utils/icon/close-big.svg';
import ThresholdReached from '../utils/icon/threshold-reached.svg';
import Image from '../utils/image'

const Cart = (props) => {
  const { cart, data, cartLoading } = props;
  const cartCloseEvent = new CustomEvent('cart:close');
  const hasLines =
    cart?.state?.lines?.length > 0 || cart.state?.items?.length > 0;
  const isEmpty = !cartLoading && !hasLines;
  const subtotal =
    parseFloat(cart.state?.subtotal?.amount) ||
    parseFloat(cart.state?.total_price) / 100;

  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get(
    'cart',
  );
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  const lines =
    cart?.mode === 'storefront'
      ? cart?.state?.lines
      : formatCartLinesAjax(cart?.state?.items);
  const [threshold, setThreshold] = useState(0);
  const [thresholdProcess, setThresholdProcess] = useState(0);
  const [gwpItems, setGwpItems] = useState(null);
  const [gwpQualifyInCart, setGwpQualifyInCart] = useState(false);
  const [gwpSampleInCart, setGwpSampleInCart] = useState(false);
  const [filteredItems, setFilteredItems] = useState(null);
  const [showGiftWrappingMessage, setShowGiftWrappingMessage] = useState(false)

  const thresholdEnabled =
    window.TMW?.localization?.currencyCode === 'CAD'
      ? data.enableThresholdCanada
      : data.enableThreshold;

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  const updateNostoCartRecs = () => {
    // eslint-disable-next-line no-console
    console.log('update nosto cart recs');
    if (!window.Nosto) return;

    window.Nosto.reloadCart();
  };

  useEffect(() => {
    document.addEventListener('cart:open', updateNostoCartRecs);
    return () => {
      document.removeEventListener('cart:open', updateNostoCartRecs);
    };
  }, []);

  useEffect(() => {
    const cartHasEligibleProduct = lines.some(
      ({ properties }) => !properties._oversized && !properties._gift_id,
    );

    if (cartHasEligibleProduct && data.giftWrappingMessage)
      setShowGiftWrappingMessage(true);
    else setShowGiftWrappingMessage(false);
  }, [lines, data])

  useEffect(updateNostoCartRecs, [cart.state?.item_count]);

  useEffect(() => {
    const threholdLeft =
      window.TMW?.localization?.currencyCode === 'CAD'
        ? data.shippingThresholdCanada - subtotal
        : data.shippingThreshold - subtotal;
    const ThresholdProcent =
      window.TMW?.localization?.currencyCode === 'CAD'
        ? (subtotal / data.shippingThresholdCanada) * 100
        : (subtotal / data.shippingThreshold) * 100;
    setThreshold(threholdLeft);
    setThresholdProcess(ThresholdProcent);
  }, [cart]);

  useEffect(() => {
    setGwpQualifyInCart(
      lines.filter((line) => {
        const attributes =
          line.attributes ||
          Object.entries(line.properties || {}).map(([key, value]) => ({
            key,
            value,
          }));
        return (
          attributes.find((attribute) => attribute.key === '_gwp_product') ||
          (attributes.find((attribute) => attribute.key === '_gwp_threshold') &&
            !attributes.find((attribute) => attribute.key === '_isSample'))
        );
      }),
    );
    setGwpSampleInCart(
      lines.filter((line) => {
        const attributes =
          line.attributes ||
          Object.entries(line.properties || {}).map(([key, value]) => ({
            key,
            value,
          }));
        return attributes.find((attribute) => attribute.key === '_isSample');
      }),
    );
    setGwpItems(
      lines.filter((line) => {
        const attributes =
          line.attributes ||
          Object.entries(line.properties || {}).map(([key, value]) => ({
            key,
            value,
          }));
        return attributes.find(
          (attribute) =>
            attribute.key === '_gwp' || attribute.key === '_nosto_sample',
        );
      }),
    );
    setFilteredItems(
      lines.filter((line) => {
        const attributes =
          line.attributes ||
          Object.entries(line.properties || {}).map(([key, value]) => ({
            key,
            value,
          }));
        return (
          !attributes.find(
            (attribute) =>
              attribute.key === '_gwp' || attribute.key === '_nosto_sample',
          ) || attributes.length === 0
        );
      }),
    );
  }, [cart]);

  const cartEmpty = (
    <div className="flex flex-col items-center justify-center gap-4 p-5 grow">
      <p className="text-black font-x-large">
        {window.TMW?.Strings?.cart?.empty}
      </p>
      {window.TMW?.Strings?.cart?.emptyBtn && (
        <a
          className="btn-primary max-w-[258px] link-unstyled"
          href={data.continue_link}
          onClick={(e) => {
            if (!data.continue_link) {
              e.preventDefault();
              document.dispatchEvent(cartCloseEvent);
            }
          }}
        >
          {window.TMW?.Strings?.cart?.emptyBtn}
        </a>
      )}
    </div>
  );

  const cartSubtotal = (
    <div className="p-5 bg-cream-grey flex flex-col gap-2.5">
      {showGiftWrappingMessage && (
        <div className="flex items-center gap-2.5">
          {data.giftWrappingMessage.image && (
            <Image src={data.giftWrappingMessage.image} />
          )}
          {data.giftWrappingMessage.text && (
            <p className="text-black font-13 leading-[17.998px]">
              {data.giftWrappingMessage.text}
            </p>
          )}
        </div>
      )}
      <div className="flex items-center justify-between w-full">
        <p className="text-black font-13-bold">
          {window.TMW?.Strings?.cart?.subtotal}
        </p>
        <p className="text-black font-13-bold">
          {cart.mode === 'storefront'
            ? formatMoney(getFormattedPriceObject(cart?.state?.subtotal || 0))
            : formatMoney(
                getFormattedPriceObject(cart?.state?.total_price || 0),
                true,
              )}
        </p>
      </div>
      <button
        className="w-full btn-primary"
        onClick={(e) => {
          e.preventDefault();
          window.location.href = cart.state?.checkoutUrl || '/checkout';
        }}
      >
        {window.TMW?.Strings?.cart?.checkout}
      </button>
    </div>
  );

  const loadingShade = useMemo(
    () => (
      <div
        className={clsx(
          'bg-pale-taupe opacity-60 absolute top-0 left-0 right-0 bottom-0 w-full h-full z-50',
          cartLoading ? 'block' : 'hidden',
        )}
      ><div className='loader absolute left-[calc(50%-25px)] top-[calc(50%-25px)]'></div></div>
    ),
    [cartLoading],
  );

  const shippingThreshold = useMemo(
    () => (
      <div className="flex relative flex-col gap-[9px] justify-center pl-5 pr-5 laptop:pr-10 before:content-[''] before:laptop:w-[calc(100%-60px)] before:w-[calc(100%-40px)] before:left-5 before:absolute before:bottom-0 before:bg-black-30 before:h-[1px] py-8">
        {threshold < 0 && (
          <p className="utility-2 text-black flex items-center justify-start gap-[9px]">
            <ThresholdReached />
            <span>{window.TMW?.Strings?.cart?.thresholdReached}</span>
          </p>
        )}
        {gwpQualifyInCart?.length > 0 && (
          <p className="utility-2 text-black flex items-center justify-start gap-[9px]">
            <ThresholdReached />
            <span>{window.TMW?.Strings?.cart?.qualifyInCart}</span>
          </p>
        )}
        {gwpSampleInCart?.length > 0 && (
          <p className="utility-2 text-black flex items-center justify-start gap-[9px]">
            <ThresholdReached />
            <span>{window.TMW?.Strings?.cart?.sampleInCart}</span>
          </p>
        )}
        {threshold > 0 && (
          <>
            <p className="text-black utility-2">
              {window.TMW?.Strings?.cart?.threshold?.replace(
                '%threshold',
                formatMoney(getFormattedPriceObject(threshold)),
              )}
            </p>
            <div className="relative w-full h-2 bg-pale-taupe">
              <div
                className="absolute top-0 left-0 h-2 transition-all bg-black"
                style={{ width: `${thresholdProcess}%` }}
              ></div>
            </div>
          </>
        )}
      </div>
    ),
    [threshold, thresholdProcess, gwpQualifyInCart, gwpSampleInCart],
  );

  return (
    <>
      <Panel
        triggers={panelTriggers}
        defaultOpen={isCartRedirect}
        context="cart"
        additionalClasses={'!h-[100dvh] !max-w-[500px]'}
        showBackdrop={true}
      >
        <div className="flex flex-col flex-grow h-full bg-neutral relative">
          <div className="flex">
            <div className="flex justify-between w-full items-center px-5 pt-5 pb-4 border-b border-black-30 min-h-[64px] laptop:min-h-[68px]">
              <div className="flex items-center justify-start gap-3">
                <div className="relative">
                  <IconCart />
                  <span
                    className="absolute top-[9.5px] laptop:top-[9px] font-base text-[10px] w-full inset-x-0 text-center text-black pointer-events-none"
                    data-cart-count
                  >
                    {cart?.state?.item_count}
                  </span>
                </div>
                <h4 className="subheading-h2 !text-[22px]">
                  {window.TMW?.Strings?.cart?.title}
                </h4>
              </div>
              <button onClick={() => document.dispatchEvent(cartCloseEvent)}>
                <CloseBig />
              </button>
            </div>
          </div>
          {thresholdEnabled && hasLines && shippingThreshold}
          {isEmpty && cartEmpty}
          {!!hasLines && (
            <div className="flex-grow pl-5 pr-5 overflow-y-scroll border-b laptop:pr-10 border-black-30">
              <>
                {filteredItems.length > 0 &&
                  filteredItems.map((line) => (
                    <CartItem key={line.id} line={line} isCartPage={true} />
                  ))}
                {gwpItems.length > 0 &&
                  gwpItems.map((line) => (
                    <CartItem
                      key={line.id}
                      line={line}
                      isCartPage={true}
                      isGwp={true}
                    />
                  ))}
              </>
            </div>
          )}
          {hasLines && cartSubtotal}
          {loadingShade}
        </div>
      </Panel>
    </>
  );
};

export default withCartContext(Cart);
